import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UNSAFE_LocationContext } from 'react-router-dom';
import type { ModuleTemplateDefinition } from '@saas/shared';
import { ensureTemplateListConfig } from '@saas/shared';
import { type QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';

import { useReferenceListContext } from '@/contexts/ReferenceListContext';
import { useOptionalToast } from '@/contexts/ToastContext';
import { api } from '@/lib/api';
import {
  MENU_ITEM_ACTIVATED_EVENT,
  type MenuItemActivatedDetail,
} from '@/lib/menu-item-activation';
import {
  extractTemplateApiErrorCode,
  moduleTemplateDetailQueryKey,
  parseTemplateTableId,
  patchTemplateViewConfig,
  seedTemplateListConfig,
  useModuleTemplateDetail,
} from '@/lib/module-templates/template-layouts';

import type {
  TableId,
  TableLayout,
  TableLayoutConfig,
  UseTableLayoutOptions,
  UseTableLayoutReturn,
} from './types';

/**
 * Hook do persystencji layoutu listy.
 *
 * Adapter dla naszych endpointów (router endpointu po prefiksie tableId):
 *  - `module-<id>` → custom module:  `/api/modules/:id/layouts[?type=list]`
 *  - `template:<code>:<entitySlug>:<viewSlug>` → widok `list` SZABLONU (hub Krok 2):
 *    odczyt z definicji szablonu w ADM (jeden syntetyczny layout; widok bez configu →
 *    zasiew kolumn z pól encji + record_name/record_number), zapis WYŁĄCZNIE na żądanie
 *    przez PATCH `.../views/[viewSlug]/config` (szew z Kroku 0: bump wersji + snapshot,
 *    dedup no-op, 409 NO_STRUCTURE / VERSION_CONFLICT → toast); create/delete layoutów
 *    niedostępne (szablon = jeden projektowany widok listy per slug widoku),
 *  - inny string (slug)  → system module: `/api/system-modules/:slug/layouts[?type=list]`
 *
 * Filtrowanie aktywnego layoutu po roli usera jest TYMCZASOWO pominięte
 * (TenantUser nie ma pola `role` — nasze role są per-permission, krok 5
 * doprecyzowuje mechanizm „widok systemowy nieruchomalny" + role-based
 * default).
 */
interface ApiLayout {
  id: string;
  name: string;
  isDefault: boolean;
  layoutConfig: Record<string, unknown> | null;
  listConfig: TableLayoutConfig | null;
  roleIds: string[] | null;
  categoryId?: string | null;
  sourceViewSlug?: string | null;
  menuSourceModuleId?: string | null;
  createdAt: string;
  updatedAt: string;
}

interface LayoutsResponse {
  layouts: ApiLayout[];
}

const EMPTY_LIST_CONFIG: TableLayoutConfig = {
  columns: [],
  defaultSort: null,
  defaultFilters: [],
  formattingRules: [],
  quickFilters: [],
  defaultViewMode: 'list',
};

/**
 * Sufiks `::layout-<id>` w tableId (akordeon kreatora menu tenanta, 2026-07-03):
 * wymusza edycję KONKRETNEGO layoutu zamiast domyślnego — panele buildera listy
 * dostają tableId z sufiksem i wszystkie trafiają w ten sam wskazany layout.
 * Endpointy liczone z tableId BEZ sufiksu; cache query key zostaje z sufiksem
 * (izolacja od zwykłego widoku tej samej tabeli).
 */
const FORCED_LAYOUT_RE = /^(.*)::layout-([^:]+)$/;

function stripForcedLayout(tableId: TableId): string {
  const m = tableId.match(FORCED_LAYOUT_RE);
  return m ? m[1]! : tableId;
}

/**
 * Odświeża cache widoków listy dla WSZYSTKICH wariantów klucza tej samej kartoteki — zwykłej
 * listy (`contractors`) i listy z wymuszonym widokiem (`contractors::layout-<id>`, Kreator Listy
 * otwarty z „Kreator ▾" albo pozycja menu). Dawniej zapis odświeżał tylko wariant edytora, więc
 * otwarta lista trzymała stary widok (np. „Rekordów na stronę") do wygaśnięcia cache.
 */
function invalidateLayoutVariants(queryClient: QueryClient, tableId: TableId): void {
  const base = stripForcedLayout(tableId);
  void queryClient.invalidateQueries({
    predicate: (q) =>
      q.queryKey[0] === 'table-layouts' &&
      typeof q.queryKey[1] === 'string' &&
      stripForcedLayout(q.queryKey[1]) === base,
  });
}

function forcedLayoutIdFrom(tableId: TableId): string | null {
  const m = tableId.match(FORCED_LAYOUT_RE);
  return m ? m[2]! : null;
}

/** Lista kartoteki modułu (`module-<id>`) — widoki pod `/modules/:id/layouts`, nie systemowe. */
export function isCustomModuleTable(tableId: TableId): boolean {
  return stripForcedLayout(tableId).startsWith('module-');
}

function moduleIdFromTableId(tableId: TableId): string {
  return stripForcedLayout(tableId).replace(/^module-/, '');
}

function listEndpointFor(tableId: TableId): string {
  return isCustomModuleTable(tableId)
    ? `/modules/${moduleIdFromTableId(tableId)}/layouts?type=list`
    : `/system-modules/${stripForcedLayout(tableId)}/layouts?type=list`;
}

/** Kolekcja widoków (POST nowy, PUT/DELETE `/<id>`) dla listy kartoteki systemowej albo modułu. */
export function collectionEndpointFor(tableId: TableId): string {
  return isCustomModuleTable(tableId)
    ? `/modules/${moduleIdFromTableId(tableId)}/layouts`
    : `/system-modules/${stripForcedLayout(tableId)}/layouts`;
}

function itemEndpointFor(tableId: TableId, layoutId: string): string {
  return isCustomModuleTable(tableId)
    ? `/modules/${moduleIdFromTableId(tableId)}/layouts/${layoutId}`
    : `/system-modules/${stripForcedLayout(tableId)}/layouts/${layoutId}`;
}

function transformLayout(layout: ApiLayout, tableId: TableId): TableLayout {
  return {
    id: layout.id,
    tableId,
    name: layout.name,
    description: null,
    isDefault: layout.isDefault,
    layoutConfig: layout.listConfig ?? EMPTY_LIST_CONFIG,
    roleIds: layout.roleIds,
    sourceViewSlug: layout.sourceViewSlug ?? null,
    menuSourceModuleId: layout.menuSourceModuleId ?? null,
    categoryId: layout.categoryId ?? null,
    createdAt: layout.createdAt,
    updatedAt: layout.updatedAt,
  };
}

export function useTableLayout(
  tableId: TableId,
  options: UseTableLayoutOptions = {},
): UseTableLayoutReturn {
  const { enabled = true } = options;
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  // Null-safe: konsumenci ConfigurableTable nie zawsze siedzą pod ToastProviderem.
  const toast = useOptionalToast();
  // Encja-odwołanie: po udanym PATCH wywołaj clobber guard (synchronizacja stanu
  // edytora bez setDirty). Null = normalne użycie (tenant / encja własna).
  const referenceCtx = useReferenceListContext();

  // Wymuszony layout z sufiksu tableId (akordeon kreatora menu tenanta).
  const forcedLayoutId = useMemo(() => forcedLayoutIdFrom(tableId), [tableId]);
  // Adres karty (null poza routerem) — `useLocation` rzuca poza routerem, kontekst nie.
  const routerLocation = useContext(UNSAFE_LocationContext) as {
    location: { search: string };
  } | null;

  // Hub Krok 2 — tryb `template`: layout pochodzi z definicji szablonu w ADM
  // (wspólny cache z edytorem struktury), nie z endpointów layoutów tenanta.
  const templateRef = useMemo(() => parseTemplateTableId(stripForcedLayout(tableId)), [tableId]);
  const templateDetailQuery = useModuleTemplateDetail(templateRef?.code, {
    enabled: enabled && templateRef !== null,
  });

  // Tryb „edycja nazwanego widoku listy kartoteki" (scope='system' osadzony poza szablonem):
  // aktywny GDY kontekst dostarcza interceptor zapisu ORAZ nie jesteśmy w trybie szablonu.
  // Ta kombinacja nie występuje w żadnym istniejącym użyciu (tenant/system-settings nie mają
  // onListConfigSave; menu-embed ma templateRef), więc gałąź jest bezpieczna przez konstrukcję.
  const referenceViewMode = templateRef === null && Boolean(referenceCtx?.onListConfigSave);

  // Aktywny layout per session (in-memory state, brak persystencji wyboru
  // per user — Krok 5 + per_user_view doprecyzuje).
  const [selectedLayoutId, setSelectedLayoutId] = useState<string | null>(null);

  // Nowa pozycja menu (inny układ wymuszony) albo inna tabela = nowy układ startowy: stary ręczny
  // wybór widoku nie może go przebijać.
  useEffect(() => {
    setSelectedLayoutId(null);
  }, [tableId, forcedLayoutId]);

  // Ponowny klik tej samej pozycji menu (karta już otwarta, adres bez zmian) — też powrót do
  // układu startowego pozycji. Dotyczy tylko tabel z układem wymuszonym przez pozycję menu.
  // Tylko karta otwarta z TEJ pozycji (`?mi=` w adresie karty — każda karta ma własny adres
  // routera) reaguje; inne otwarte karty zachowują ręczny wybór. Kontekst adresu czytany wprost,
  // bo hook bywa używany także poza routerem (wtedy brak `mi` = brak resetu).
  const ownMenuItemId = useMemo(
    () => new URLSearchParams(routerLocation?.location.search ?? '').get('mi'),
    [routerLocation?.location.search],
  );
  useEffect(() => {
    if (!forcedLayoutId || !ownMenuItemId) return;
    const reset = (e: Event) => {
      const detail = (e as CustomEvent<MenuItemActivatedDetail>).detail;
      if (detail?.menuItemId === ownMenuItemId) setSelectedLayoutId(null);
    };
    window.addEventListener(MENU_ITEM_ACTIVATED_EVENT, reset);
    return () => window.removeEventListener(MENU_ITEM_ACTIVATED_EVENT, reset);
  }, [forcedLayoutId, ownMenuItemId]);

  // Fetch layouts (tenant/system — wyłączone w trybie template)
  const { data: tenantData, isLoading: tenantLoading } = useQuery({
    queryKey: ['table-layouts', tableId],
    queryFn: async () => {
      const res = await api.get<LayoutsResponse>(listEndpointFor(tableId));
      const layouts = res.data.layouts.map((l) => transformLayout(l, tableId));
      return { layouts };
    },
    enabled: enabled && !!tableId && templateRef === null && !referenceViewMode,
    staleTime: 5 * 60 * 1000, // 5 minut
  });

  // Tryb template: jeden syntetyczny layout z configu widoku `list` szablonu.
  // Widok bez configu (lub z pustymi kolumnami) → zasiew kolumn z definicji pól
  // encji + wbudowane record_name/record_number (panele nie mają „dodaj kolumnę").
  // Encja-odwołanie: entity.fields jest puste — seed korzysta z kartotekaFields
  // z ReferenceListContext (jeśli dostępny), żeby kolumny kartoteki pojawiły się
  // od razu po pierwszym otwarciu kreatora.
  const templateData = useMemo<{ layouts: TableLayout[] } | undefined>(() => {
    if (!templateRef) return undefined;
    const tpl = templateDetailQuery.data;
    if (!tpl) return undefined;
    const entity = tpl.entities.find((e) => e.slug === templateRef.entitySlug);
    if (!entity) return { layouts: [] };
    const view = entity.views.find((v) => v.slug === templateRef.viewSlug && v.type === 'list');
    // A (Etap 2): encja-odwołanie do kartoteki BEZ zapisanego slotu widoku listy —
    // NIE przerywamy (jak builder formularza, który samozasiewa z definicji kartoteki).
    // Syntetyzujemy domyślny widok z kartotekaDefaultListConfig; slot powstanie przy
    // pierwszym zapisie (backend create-on-missing).
    //
    // Fix new_table: stary warunek `entity.fields.length === 0` wycinał new_table
    // (która ma własne pola > 0), dając isReferenceWithCtx=false → pusto.
    // Poprawka: seed gdy kontekst menu-embed (referenceCtx !== null) — niezależnie od
    // liczby własnych pól. Gałąź `else { seedTemplateListConfig(entity) }` niżej
    // obsługuje new_table z polami poprawnie. Encje własne bez kontekstu: bez zmian.
    const isReferenceWithCtx = referenceCtx !== null;
    if (!view && !isReferenceWithCtx) return { layouts: [] };
    const existing = view ? ensureTemplateListConfig(view.listConfig) : null;
    let listConfig: TableLayoutConfig;
    if (existing && Array.isArray(existing.columns) && existing.columns.length > 0) {
      listConfig = existing;
    } else if (entity.fields.length === 0 && referenceCtx) {
      // Encja-odwołanie do kartoteki systemowej (brak własnych pól).
      // Etap 2 B: preferujemy domyślny listConfig z API kartoteki (buildDefaultListConfig —
      // slug-driven: record_name + showInList pola), który odzwierciedla intencję ITE.
      // Fallback: zasiew ze wszystkich pól kartoteki (stara ścieżka).
      if (referenceCtx.kartotekaDefaultListConfig) {
        listConfig = referenceCtx.kartotekaDefaultListConfig as TableLayoutConfig;
      } else if (referenceCtx.kartotekaFields.length > 0) {
        const kartotekaColumns = [...referenceCtx.kartotekaFields]
          .sort((a, b) => a.sortOrder - b.sortOrder)
          .map((f, i) => ({ id: f.slug, visible: true, width: 'auto' as const, order: i + 2 }));
        const seeded: TableLayoutConfig = {
          columns: [
            { id: 'record_name', visible: true, width: 'auto', order: 0 },
            { id: 'record_number', visible: true, width: 'auto', order: 1 },
            ...kartotekaColumns,
          ],
          defaultSort: null,
          defaultFilters: [],
          formattingRules: [],
          quickFilters: [],
          defaultViewMode: 'list',
        };
        listConfig = seeded;
      } else {
        listConfig = seedTemplateListConfig(entity);
      }
    } else {
      listConfig = seedTemplateListConfig(entity);
    }
    return {
      layouts: [
        {
          id: tableId,
          tableId,
          name: view?.name ?? 'Lista',
          description: null,
          isDefault: true,
          layoutConfig: listConfig,
          roleIds: null,
          createdAt: '',
          updatedAt: '',
        },
      ],
    };
  }, [templateRef, templateDetailQuery.data, tableId, referenceCtx]);

  // Tryb reference-view: jeden syntetyczny layout z configu przekazanego przez kontekst
  // (aktualny config nazwanego widoku listy kartoteki). Zapis przechwytuje onListConfigSave;
  // seed odświeża parent, aktualizując `initialListConfig` po każdym zapisie (brak clobberu).
  const referenceViewData = useMemo<{ layouts: TableLayout[] } | undefined>(() => {
    if (!referenceViewMode || !referenceCtx) return undefined;
    const seed = (referenceCtx.initialListConfig ??
      referenceCtx.kartotekaDefaultListConfig ?? {
        columns: [],
        defaultSort: null,
        defaultFilters: [],
        formattingRules: [],
        quickFilters: [],
        defaultViewMode: 'list',
      }) as TableLayoutConfig;
    return {
      layouts: [
        {
          id: tableId,
          tableId,
          name: 'Lista',
          description: null,
          isDefault: true,
          layoutConfig: seed,
          roleIds: null,
          createdAt: '',
          updatedAt: '',
        },
      ],
    };
  }, [referenceViewMode, referenceCtx, tableId]);

  const data = templateRef ? templateData : referenceViewMode ? referenceViewData : tenantData;
  const isLoading = templateRef
    ? templateDetailQuery.isLoading
    : referenceViewMode
      ? false
      : tenantLoading;

  // Wybor aktywnego layoutu: 0) wybrany przez usera w selektorze widoków, 1) wymuszony
  // sufiksem tableId (pozycja menu / kreator menu tenanta — startowy, bez fallbacku żeby zapis
  // nie trafił w cudzy layout), 2) is_default, 3) pierwszy.
  const activeLayout = useMemo<TableLayout | null>(() => {
    const layouts = data?.layouts || [];
    if (layouts.length === 0) return null;

    // Jawny wybór użytkownika (selektor widoków nad tabelą) wygrywa z wymuszonym — wymuszony
    // layout z pozycji menu jest układem STARTOWYM (decyzja 2026-10-05), a nie blokadą.
    if (selectedLayoutId) {
      const selected = layouts.find((l) => l.id === selectedLayoutId);
      if (selected) return selected;
    }

    if (forcedLayoutId) {
      return layouts.find((l) => l.id === forcedLayoutId) ?? null;
    }

    const defaultLayout = layouts.find((l) => l.isDefault);
    if (defaultLayout) return defaultLayout;

    return layouts[0] ?? null;
  }, [data?.layouts, selectedLayoutId, forcedLayoutId]);

  // Zapis biezacego layoutu (PUT) lub utworz nowy 'Default' gdy zaden nie istnieje.
  // Tryb template: PATCH configu widoku szablonu (szew z Kroku 0) — na żądanie,
  // z dedupem no-op po stronie API i 409 przy konflikcie równoległych zapisów.
  const saveMutation = useMutation({
    mutationFn: async (listConfig: TableLayoutConfig) => {
      // Interceptor zapisu (menu-embed w szablonie LUB nazwany widok listy kartoteki):
      // gdy kontekst go dostarcza, zapis idzie do parenta zamiast do API. Wynik void —
      // saveMutation.onSuccess wciąż się odpali. Sprawdzenie na górze, bo dotyczy obu trybów.
      if (referenceCtx?.onListConfigSave) {
        referenceCtx.onListConfigSave(listConfig);
        return;
      }
      if (templateRef) {
        return patchTemplateViewConfig(
          templateRef.code,
          templateRef.entitySlug,
          templateRef.viewSlug,
          { listConfig },
        );
      }
      if (!activeLayout?.id) {
        // Brak layoutu — tworz 'Default'.
        const res = await api.post(collectionEndpointFor(tableId), {
          layoutType: 'list',
          name: 'Default',
          isDefault: true,
          layoutConfig: {},
          listConfig,
        });
        return res.data;
      }
      const res = await api.put(itemEndpointFor(tableId, activeLayout.id), { listConfig });
      return res.data;
    },
    onSuccess: (_, listConfig) => {
      if (templateRef) {
        if (referenceCtx?.onListConfigSave) {
          // Ścieżka intercept (menu-embed): zapis poszedł do localEntities u parenta,
          // NIE do backendu. Zamiast invalidować (co pobiera stary stan z serwera i
          // nadpisuje świeżo zapisany config), aktualizujemy cache OPTYMISTYCZNIE —
          // tak żeby przy ponownym otwarciu modalu templateData odczytało nowy listConfig.
          queryClient.setQueryData<ModuleTemplateDefinition>(
            moduleTemplateDetailQueryKey(templateRef.code),
            (prev) => {
              if (!prev) return prev;
              return {
                ...prev,
                entities: prev.entities.map((e) => {
                  if (e.slug !== templateRef.entitySlug) return e;
                  const hasView = e.views.some(
                    (v) => v.slug === templateRef.viewSlug && v.type === 'list',
                  );
                  if (!hasView) {
                    // Widok jeszcze nie istnieje w cache szablonu (np. pierwszy zapis
                    // przez menu-embed). Nie syntetyzujemy placeholdera z nazwą=slug —
                    // zapis idzie do parenta (onListConfigSave), który aktualizuje
                    // lokalny stan. Zwracamy encję bez zmian; widok pojawi się po
                    // następnym pełnym fetchu szczegółu szablonu.
                    return e;
                  }
                  return {
                    ...e,
                    views: e.views.map((v) =>
                      v.slug === templateRef.viewSlug && v.type === 'list'
                        ? { ...v, listConfig }
                        : v,
                    ),
                  };
                }),
              };
            },
          );
          // Clobber guard: synchronizuj stan edytora BEZ setDirty.
          referenceCtx.onViewConfigPersisted(listConfig);
          return;
        }
        // Ścieżka realna (PATCH do backendu): clobber guard + invalidate cache.
        if (referenceCtx) {
          referenceCtx.onViewConfigPersisted(listConfig);
        }
        // Odśwież wspólny cache szczegółu szablonu (edytor struktury + panele).
        void queryClient.invalidateQueries({
          queryKey: moduleTemplateDetailQueryKey(templateRef.code),
        });
        return;
      }
      // Tryb reference-view (nazwany widok listy kartoteki): zapis poszedł przez interceptor
      // do parenta; synchronizujemy stan edytora bez setDirty (clobber guard).
      if (referenceCtx?.onListConfigSave) {
        referenceCtx.onViewConfigPersisted(listConfig);
        return;
      }
      invalidateLayoutVariants(queryClient, tableId);
    },
    onError: (err) => {
      if (!templateRef) return; // tenant/system: panele obsługują błąd po staremu
      const errorCode = extractTemplateApiErrorCode(err);
      toast?.show({
        variant: 'error',
        title:
          errorCode === 'VERSION_CONFLICT'
            ? t('moduleTemplateEditor.layoutDesigner.errors.versionConflict')
            : errorCode === 'NO_STRUCTURE'
              ? t('moduleTemplateEditor.layoutDesigner.errors.noStructure')
              : t('moduleTemplateEditor.layoutDesigner.errors.saveError'),
      });
    },
  });

  // Tworzy nowy layout (obok obecnych) — uzywane przez admina do tworzenia
  // widokow per rola. Niedostepne w trybie template (jeden widok per slug).
  const createMutation = useMutation({
    mutationFn: async ({ name, roleIds }: { name: string; roleIds?: string[] }) => {
      if (templateRef) throw new Error('Layouty per rola nie istnieją dla widoku szablonu');
      const currentConfig: TableLayoutConfig = activeLayout?.layoutConfig ?? EMPTY_LIST_CONFIG;
      const res = await api.post(collectionEndpointFor(tableId), {
        layoutType: 'list',
        name,
        isDefault: false,
        layoutConfig: {},
        listConfig: currentConfig,
        roleIds: roleIds ?? [],
      });
      return res.data;
    },
    onSuccess: () => {
      invalidateLayoutVariants(queryClient, tableId);
    },
  });

  // Usuwa layout (admin only po stronie backendu — `crm_core.*.manage`).
  // Niedostepne w trybie template.
  const deleteMutation = useMutation({
    mutationFn: async (layoutId: string) => {
      if (templateRef) throw new Error('Widok szablonu nie ma layoutów do usunięcia');
      const res = await api.delete(itemEndpointFor(tableId, layoutId));
      return res.data;
    },
    onSuccess: () => {
      invalidateLayoutVariants(queryClient, tableId);
    },
  });

  const saveLayout = useCallback(
    async (config: TableLayoutConfig, opts?: { throwOnForbidden?: boolean }) => {
      try {
        await saveMutation.mutateAsync(config);
      } catch (err) {
        // Zapis layoutu listy wymaga uprawnienia `crm_core.custom_modules.manage`. User bez
        // niego (np. rola Członek) auto-zapisuje layout w tle → 403. Łykamy cicho (używa
        // domyślnego/istniejącego layoutu, bez persystencji), inne błędy przepuszczamy dalej.
        // Jawny zapis z Kreatora (`throwOnForbidden`) musi się dowiedzieć o odmowie —
        // inaczej pokaże „Zapisano", a zmiana nie trafi do listy.
        if (isAxiosError(err) && err.response?.status === 403 && !opts?.throwOnForbidden) return;
        throw err;
      }
    },
    [saveMutation],
  );

  const createLayout = useCallback(
    async (name: string, roleIds?: string[]) => {
      await createMutation.mutateAsync({ name, roleIds });
    },
    [createMutation],
  );

  const deleteLayout = useCallback(
    async (layoutId: string) => {
      await deleteMutation.mutateAsync(layoutId);
    },
    [deleteMutation],
  );

  const switchLayout = useCallback((layoutId: string) => {
    setSelectedLayoutId(layoutId);
  }, []);

  const resetLayout = useCallback(() => {
    setSelectedLayoutId(null);
  }, []);

  return {
    layout: activeLayout,
    availableLayouts: data?.layouts || [],
    isLoading,
    saveLayout,
    createLayout,
    deleteLayout,
    switchLayout,
    resetLayout,
    isSaving: saveMutation.isPending || createMutation.isPending || deleteMutation.isPending,
  };
}
